import GenericTable from "components/table";
import {
  Feedback,
  FeedbackDetail,
  FeedbackStatusEnum,
  FeedbackTypeEnum,
} from "types";
import React, { useEffect, useState } from "react";
import { Check, Clock, ThumbsDown, ThumbsUp, Trash, X } from "lucide-react";
import { Dialog } from "components/dialog";
import { useConfirmation } from "components/dialog/confirmation";
import {
  approveFeedback,
  deleteFeedback,
  fetchFeedbackById,
  rejectFeedback,
} from "api/axios";
import toast from "react-hot-toast";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import { handleAxiosAndCaughtError } from "utils";
import TooltipHorizon from "components/tooltip";
import { FilterOption } from "components/table/FilterDropdown";
import { useSidebarCountsContext } from "contexts/SidebarCountsContext";
import { useTranslation } from "react-i18next";
import {
  getStatusTranslationKey,
  getTypeTranslationKey,
} from "utils/i18n-helpers";

type FeedbackTableProps = {
  tableData: Feedback[];
  fetchFeedbacks: () => Promise<void>;
  filterOptions?: {
    feedback_type?: FilterOption[];
    feedback_status?: FilterOption[];
  };
  initialFeedbackId?: string | null;
};

const FeedbackTable: React.FC<FeedbackTableProps> = ({
  tableData,
  fetchFeedbacks,
  filterOptions,
  initialFeedbackId,
}) => {
  const { t } = useTranslation();
  const [updatingRowId, setUpdatingRowId] = React.useState<string | null>(null);
  const { refetchFeedbackCount } = useSidebarCountsContext();

  const { confirm } = useConfirmation();

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] =
    useState<FeedbackDetail | null>(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  const handleViewFeedbackDetails = async (feedbackId: string) => {
    setLoadingFeedback(true);
    try {
      // Fetch detailed feedback from API
      const detailedFeedback = await fetchFeedbackById(feedbackId);
      setSelectedFeedback(detailedFeedback);
      setIsViewDialogOpen(true);
    } catch (err) {
      handleAxiosAndCaughtError(err);
      // Fallback to table data if API fails
      setSelectedFeedback(null);
      setIsViewDialogOpen(true);
    } finally {
      setLoadingFeedback(false);
    }
  };

  // Auto-open dialog if initialFeedbackId is provided (from notification)
  useEffect(() => {
    if (initialFeedbackId && !isViewDialogOpen) {
      handleViewFeedbackDetails(initialFeedbackId);
    }
  }, [initialFeedbackId]);

  const handleDeleteFeedback = async (feedbackId: string) => {
    const confirmed = await confirm({
      title: t("feedback.deleteFeedback"),
      message: t("feedback.deleteFeedbackMessage"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      setUpdatingRowId(feedbackId);
      try {
        await deleteFeedback(feedbackId);
        await fetchFeedbacks();
        toast.success(t("feedback.deletedSuccessfully"));
        setIsViewDialogOpen(false);
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setUpdatingRowId(null);
      }
    }
  };

  const getStatusColor = (status: FeedbackStatusEnum) => {
    switch (status) {
      case FeedbackStatusEnum.PENDING:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case FeedbackStatusEnum.APPROVED:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case FeedbackStatusEnum.REJECTED:
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getStatusIcon = (status: FeedbackStatusEnum) => {
    switch (status) {
      case FeedbackStatusEnum.PENDING:
        return <Clock size={12} />;
      case FeedbackStatusEnum.APPROVED:
        return <Check size={12} />;
      case FeedbackStatusEnum.REJECTED:
        return <X size={12} />;
      default:
        return <Clock size={12} />;
    }
  };

  const getFeedbackTypeColor = (type: FeedbackTypeEnum) => {
    switch (type) {
      case FeedbackTypeEnum.CONTRIBUTE:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case FeedbackTypeEnum.DISLIKE:
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getFeedbackTypeIcon = (type: FeedbackTypeEnum) => {
    switch (type) {
      case FeedbackTypeEnum.CONTRIBUTE:
        return <ThumbsUp size={12} />;
      case FeedbackTypeEnum.DISLIKE:
        return <ThumbsDown size={12} />;
      default:
        return <ThumbsUp size={12} />;
    }
  };

  const columns = [
    {
      accessorKey: "feedback_type",
      header: () => <p>{t("library.type")}</p>,
      cell: (info: any) => {
        const type = info.getValue() as FeedbackTypeEnum;
        return (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${getFeedbackTypeColor(
              type
            )}`}
          >
            {getFeedbackTypeIcon(type)}
            {t(getTypeTranslationKey(type))}
          </span>
        );
      },
      meta: {
        filterable: true,
        filterOptions: filterOptions?.feedback_type,
      },
      filterFn: "multiSelect",
    },
    {
      accessorKey: "feedback_status",
      header: () => <p>{t("library.status")}</p>,
      cell: (info: any) => {
        const status = info.getValue() as FeedbackStatusEnum;
        return (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(
              status
            )}`}
          >
            {getStatusIcon(status)}
            {t(getStatusTranslationKey(status))}
          </span>
        );
      },
      meta: {
        filterable: true,
        filterOptions: filterOptions?.feedback_status,
      },
      filterFn: "multiSelect",
    },
    {
      accessorKey: "created_at",
      header: () => <p>{t("library.createdDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {dayjs(info.getValue()).format(DATE_TIME_FORMAT)}
        </p>
      ),
    },
    {
      accessorKey: "updated_at",
      header: () => <p>{t("library.updatedDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {dayjs(info.getValue()).format(DATE_TIME_FORMAT)}
        </p>
      ),
    },
    {
      accessorKey: "actions",
      header: () => <p></p>,
      cell: (info: any) => {
        const feedback = info.row.original as Feedback;
        return (
          <div className="flex items-center gap-4">
            <TooltipHorizon
              trigger={
                <button
                  disabled={updatingRowId === feedback.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFeedback(feedback.id);
                  }}
                  className="rounded-md p-2 text-red-500 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900"
                >
                  <Trash size={16} />
                </button>
              }
              content={<div>{t("feedback.removeFeedback")}</div>}
              placement="top"
              extra=""
            />
          </div>
        );
      },
    },
  ];

  const dataWithActions = tableData.map((row) => ({
    ...row,
    updatingRowId,
  }));

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return updatingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  const isFeedbackEditable = (feedback: Feedback | null) => {
    return feedback?.feedback_status === FeedbackStatusEnum.PENDING;
  };

  const handleApproveFeedback = async (feedbackId: string) => {
    const confirmed = await confirm({
      title: t("feedback.approveFeedback"),
      message: t("feedback.approveFeedbackMessage"),
      confirmText: t("feedback.approve"),
      cancelText: t("common.cancel"),
      variant: "default",
    });

    if (confirmed) {
      setUpdatingRowId(feedbackId);
      try {
        await approveFeedback(feedbackId);
        await fetchFeedbacks();
        await refetchFeedbackCount();
        toast.success(t("feedback.approvedSuccessfully"));
        setIsViewDialogOpen(false);
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setUpdatingRowId(null);
      }
    }
  };

  const handleRejectFeedback = async (feedbackId: string) => {
    const confirmed = await confirm({
      title: t("feedback.rejectFeedback"),
      message: t("feedback.rejectFeedbackMessage"),
      confirmText: t("feedback.reject"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      setUpdatingRowId(feedbackId);
      try {
        await rejectFeedback(feedbackId);
        await fetchFeedbacks();
        await refetchFeedbackCount();
        toast.success(t("feedback.rejectedSuccessfully"));
        setIsViewDialogOpen(false);
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setUpdatingRowId(null);
      }
    }
  };

  return (
    <>
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        searchPlaceholder={t("feedback.searchFeedbacks")}
        debug={true}
        renderRowClassName={renderRowClassName}
        onRowClick={(item) => {
          handleViewFeedbackDetails(item.id);
        }}
      />

      {/* View Feedback Dialog */}
      <Dialog
        isOpen={isViewDialogOpen}
        setIsOpen={setIsViewDialogOpen}
        outsideClickDisabled={true}
        backdropHandlerDisabled={true}
      >
        <div className="flex min-h-[60vh] w-full max-w-[700px] flex-col gap-4 sm:gap-6">
          <div className="border-b border-gray-200 pb-3 sm:pb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("library.feedbackDetails")}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
              {t("library.viewFeedbackDetails")}
            </p>
          </div>

          {loadingFeedback ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
                <p className="mt-2 text-sm text-gray-500">
                  {t("library.loadingFeedback")}
                </p>
              </div>
            </div>
          ) : selectedFeedback ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.type")}
                  </label>
                  <div className="mt-1">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${getFeedbackTypeColor(
                        selectedFeedback.feedback_type
                      )}`}
                    >
                      {getFeedbackTypeIcon(selectedFeedback.feedback_type)}
                      {t(
                        `feedback.${String(
                          selectedFeedback.feedback_type
                        ).toLowerCase()}`
                      )}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.status")}
                  </label>
                  <div className="mt-1">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(
                        selectedFeedback.feedback_status
                      )}`}
                    >
                      {getStatusIcon(selectedFeedback.feedback_status)}
                      {t(
                        `feedback.${String(
                          selectedFeedback.feedback_status
                        ).toLowerCase()}`
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.createdDate")}
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {dayjs(selectedFeedback.created_at).format(
                      DATE_TIME_FORMAT
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.updatedDate")}
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {dayjs(selectedFeedback.updated_at).format(
                      DATE_TIME_FORMAT
                    )}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("library.refinedMessage")}
                </label>
                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800">
                  <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
                    {selectedFeedback.refined_message}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("library.response")}
                </label>
                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800">
                  <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
                    {selectedFeedback.ai_response}
                  </p>
                </div>
              </div>
              {selectedFeedback.feedback_type === FeedbackTypeEnum.DISLIKE && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.feedbackMessage")}
                  </label>
                  <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800">
                    <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
                      {selectedFeedback.feedback_message}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
                {isFeedbackEditable(selectedFeedback) && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedFeedback) {
                          handleApproveFeedback(selectedFeedback.id);
                        }
                      }}
                      className="flex-1 rounded-lg bg-green-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-20"
                    >
                      {t("feedback.approve")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedFeedback) {
                          handleRejectFeedback(selectedFeedback.id);
                        }
                      }}
                      className="flex-1 rounded-lg bg-red-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-20"
                    >
                      {t("feedback.reject")}
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </Dialog>
    </>
  );
};

export default FeedbackTable;
